import { addMinutes, format, parse, subMinutes } from "date-fns";

/**
 *
 * @param rawDate - Date or timestamp
 * @description This function normalizes a date to UTC by adjusting it based on the local timezone offset.
 * It takes a Date object or a timestamp (number) as input and returns a new Date object that represents the same moment in time, but in UTC.
 *
 * Why do we need this?
 * - Our backend when the frontend sends a timestamp, if they need to convert it to Date object, their converter will convert it to the UTC date.
 * - This function is used to normalize the date to UTC before sending it to the backend.
 *
 * In our case, when we send 23 March 2025 00:00:00 to the backend, it will be converted to 22 March 2025 17:00:00 UTC.
 * @returns
 */
export function normalizeUtcDateTime(rawDate: Date | number): Date {
  const date = new Date(rawDate);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date");
  }

  // getTimezoneOffset returns the difference in minutes between UTC and the local time
  const timezoneOffset = date.getTimezoneOffset();
  const normalizedDate = subMinutes(date, timezoneOffset);
  return normalizedDate;
}

/**
 * As of v2, date-fns use Unicode Technical Standard #35
 *
 * read more
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 */

export const JAVA_DATE = "yyyy-MM-dd";
// This should be removed once BE has changed all format to JAVA_DATE
export const JAVA_DATE_ALT = "MM-dd-yyyy";
export const DATE = "dd-MM-yyyy";

export const TIME = "HH:mm";

export function changeFormat(
  date: string,
  from: string,
  to: string,
  baseDate: number | Date = 0
) {
  return format(parse(date, from, baseDate), to);
}

export function formatWithOffset(dateString: string, dateFormat: string) {
  const date = new Date(dateString.replace(" ", "T"));
  return format(addMinutes(date, -date.getTimezoneOffset()), dateFormat);
}

export function clampDate(date: Date, minDate: Date, maxDate: Date) {
  if (date < minDate) return minDate;
  if (date > maxDate) return maxDate;
  return date;
}
